import { format, isToday, isYesterday } from 'date-fns';
import type { HistoryEntry, HistoryKind, ViewportHistoryEvent } from '../types/history';

type Context = {modelName: string;manualPlacement: boolean;aiRegions: number | null;};

export function describeEvent(event: ViewportHistoryEvent, ctx: Context): Pick<HistoryEntry, 'kind' | 'title' | 'description'> {
  const n = event.touchpoints ?? 0;
  switch (event.type) {
    case 'imported':
      return { kind: 'import', title: 'Imported model', description: ctx.modelName };
    case 'duplicated':
      return { kind: 'import', title: 'Duplicated model', description: 'Copied from the original with its orientation and protection' };
    case 'oriented':
      if (event.protectedExposure != null) {
        const clear = Math.round((1 - event.protectedExposure) * 100);
        return { kind: 'orient', title: 'Auto-oriented model', description: `Protected surfaces turned away from supports (${clear}% support-free), tilted 18°` };
      }
      return { kind: 'orient', title: 'Auto-oriented model', description: 'Detailed side turned toward the platform, tilted 18°' };
    case 'supports': {
      const avoided = event.protectedAvoided ? `, avoided ${event.protectedAvoided} on protected areas` : '';
      if (event.mode === 'full') return { kind: 'supports', title: 'Generated supports (1-Click)', description: `Added ${n} touchpoints${avoided} and centered on platform` };
      if (ctx.manualPlacement) return { kind: 'manual', title: 'Created manual supports', description: `Added ${n} touchpoints, modified settings` };
      return { kind: 'supports', title: 'Generated supports (Auto)', description: `Added ${n} touchpoints${avoided || ' with current settings'}` };
    }
    case 'cleared':
      return { kind: 'clear', title: 'Cleared supports', description: 'Removed all supports and the raft' };
    case 'moved':
      return { kind: 'move', title: 'Moved model', description: 'Repositioned on the build platform' };
    case 'protected':
      return {
        kind: 'protect',
        title: 'Protected surfaces',
        description: ctx.aiRegions ? `AI protected ${ctx.aiRegions} cog ${ctx.aiRegions === 1 ? 'area' : 'areas'}` : 'Painted protected areas'
      };
  }
}

export const timeLabel = (t: number) => format(t, 'HH:mm');

export function dayLabel(t: number): string {
  if (isToday(t)) return 'Today';
  if (isYesterday(t)) return 'Yesterday';
  return format(t, 'EEEE, MMM d');
}

export function groupByDay(entries: HistoryEntry[]): {day: string;isToday: boolean;items: HistoryEntry[];}[] {
  const groups: {day: string;isToday: boolean;items: HistoryEntry[];}[] = [];
  entries.forEach((e) => {
    const day = dayLabel(e.time);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.items.push(e);else
    groups.push({ day, isToday: isToday(e.time), items: [e] });
  });
  return groups;
}

export const kindLabel: Record<HistoryKind, string> = {
  import: 'Import',
  orient: 'Orient',
  supports: 'Support',
  manual: 'Support',
  clear: 'Support',
  protect: 'Protect',
  move: 'Layout',
  restore: 'History'
};
